import {useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {TableCell, TableRow, Menu, MenuItem} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ButtonBase from '@mui/material/ButtonBase';
import {
  updateUserRole,
  updateUserStatus,
} from '../../redux/actions/userActions';
import {Can} from '../common/Can';
import {
  ROLE_ADMINISTRATOR,
  ROLE_USER_ADMIN,
  ROLE_USER,
} from '../../utils/constants';

function UserRow({data}) {
  const dispatch = useDispatch();
  const [userAnchorEl, setUserAnchorEl] = useState(null);
  const [roleAnchorEl, setRoleAnchorEl] = useState(null);

  const handleUserMenuOpen = (event) => {
    setUserAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserAnchorEl(null);
  };

  const handleUserAction = (action) => {
    dispatch(updateUserStatus(data.id, action));
    handleUserMenuClose();
  };

  const handleRoleMenuOpen = (event) => {
    setRoleAnchorEl(event.currentTarget);
  };

  const handleRoleMenuClose = () => {
    setRoleAnchorEl(null);
  };

  const handleRoleAction = (action) => {
    dispatch(updateUserRole(data.id, action));
    handleRoleMenuClose();
  };

  const displayRoles = {
    [ROLE_ADMINISTRATOR]: 'Administrator',
    [ROLE_USER]: 'User',
    [ROLE_USER_ADMIN]: 'User Admin',
  };

  return (
    <TableRow id={data.id}>
      <TableCell>{data.displayName}</TableCell>
      <TableCell>{data.userPrincipalName}</TableCell>
      <TableCell>
        <Can I="update" a="user" passThrough>
          {(canUpdateUser) => (
            <ButtonBase
              disabled={!canUpdateUser}
              onClick={handleUserMenuOpen}
              onKeyDown={(e) => e.key === 'Enter' && handleUserMenuOpen(e)}
              style={{
                paddingRight: canUpdateUser ? '' : '5ch',
              }}
            >
              <span>{data.provisionStatus ? 'Licensed' : 'Not Licensed'}</span>
              {data.provisionStatus ? (
                <VerifiedOutlinedIcon color="success" style={{marginLeft: 8}} />
              ) : (
                <CancelOutlinedIcon color="error" style={{marginLeft: 8}} />
              )}
              {canUpdateUser && (
                <ExpandMoreIcon
                  color="action"
                  style={{
                    marginLeft: 8,
                    transform: userAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              )}
            </ButtonBase>
          )}
        </Can>
        <Menu
          anchorEl={userAnchorEl}
          open={Boolean(userAnchorEl)}
          onClose={handleUserMenuClose}
          anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          transformOrigin={{vertical: 'top', horizontal: 'right'}}
          sx={{'& .MuiPaper-root': {width: 150}}}
        >
          {!data.provisionStatus && (
            <MenuItem onClick={() => handleUserAction('activate')}>
              Activate
            </MenuItem>
          )}
          {data.provisionStatus && (
            <MenuItem onClick={() => handleUserAction('deactivate')}>
              Deactivate
            </MenuItem>
          )}
          {data.provisionStatus && (
            <MenuItem onClick={() => handleUserAction('reregister')}>
              Reregister
            </MenuItem>
          )}
          {data.provisionStatus && (
            <MenuItem onClick={() => handleUserAction('refresh')}>
              Refresh
            </MenuItem>
          )}
        </Menu>
      </TableCell>
      <Can I="promote" a="user" passThrough>
        {(canPromoteUser) => (
          <TableCell>
            <ButtonBase
              disabled={data.role === ROLE_ADMINISTRATOR || !canPromoteUser}
              onClick={handleRoleMenuOpen}
              onKeyDown={(e) => e.key === 'Enter' && handleRoleMenuOpen(e)}
              style={{
                alignItems: 'center',
                paddingRight: canPromoteUser ? '' : '5ch',
              }}
            >
              <span>{displayRoles[data.role]}</span>
              {canPromoteUser && (
                <ExpandMoreIcon
                  color={
                    data.role === ROLE_ADMINISTRATOR ? 'disabled' : 'action'
                  }
                  style={{
                    marginLeft: 8,
                    transform: roleAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              )}
            </ButtonBase>
            {data.role !== ROLE_ADMINISTRATOR && (
              <Menu
                anchorEl={roleAnchorEl}
                open={Boolean(roleAnchorEl)}
                onClose={handleRoleMenuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                sx={{'& .MuiPaper-root': {width: 200}}}
              >
                {data.role === ROLE_USER && (
                  <MenuItem onClick={() => handleRoleAction('promote')}>
                    Grant User Admin
                  </MenuItem>
                )}
                {data.role === ROLE_USER_ADMIN && (
                  <MenuItem onClick={() => handleRoleAction('demote')}>
                    Revoke User Admin
                  </MenuItem>
                )}
              </Menu>
            )}
          </TableCell>
        )}
      </Can>
    </TableRow>
  );
}

UserRow.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    userPrincipalName: PropTypes.string.isRequired,
    provisionStatus: PropTypes.bool.isRequired,
    role: PropTypes.string.isRequired,
  }).isRequired,
};

export default UserRow;
